import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import "./fundsDeposited.scss";
import { useDispatch, useSelector } from "react-redux";
import { getFundsDeposited } from "../../../services/fundraiserService";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import debounce from "lodash.debounce";

const FundsDepositedTable = () => {
  const dispatch = useDispatch();
  const debit = useSelector((state) => state?.fundraiserReducer?.transactions?.debit);
  const [allFunds, setAllFunds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const getFundsData = async (query = "") => {
    try {
      const fundsPromise = await dispatch(getFundsDeposited({ search: query, silent: true }))?.unwrap();
      if (fundsPromise?.success === true) {
        setAllFunds(fundsPromise?.data || []);
        setCurrentPage(fundsPromise?.currentPage || 1);
        setTotalPages(fundsPromise?.totalPages || 1);
        setCount(fundsPromise?.count || 0);
      } else {
        setAllFunds([]);
      }
    } catch (e) {
      // Silently handle errors for empty fundraiser accounts
      setAllFunds([]);
    }
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query) => {
        getFundsData(query);
      }, 300), // 300ms debounce time
    []
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    getFundsData(searchQuery);
  };

  useEffect(() => {
    getFundsData(searchQuery);
  }, [currentPage]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const columns = [
    {
      name: "ID",
    },
    {
      name: "Date",
    },
    {
      name: "Withdrawal Amount",
    }
  ];

  return (
    <div className="col-span-12">
      {/* Card list on mobile */}
      <div className="sm:hidden space-y-3 mt-2">
        {Array.isArray(debit) && debit.length > 0 ? (
          debit.map((item, idx) => (
            <div key={idx} className="bg-white rounded-[10px] border border-[#EAEAEA] p-4 shadow-sm">
              <div className="flex justify-between text-[11px] text-[#8C8C8C]"><span>ID</span><span className="text-[#303030] font-medium truncate max-w-[65%]">{item?._id}</span></div>
              <div className="h-px bg-[#F1F1F1] my-2" />
              <div className="flex justify-between text-[11px] text-[#8C8C8C]"><span>Date</span><span className="text-[#303030] font-medium">{separateDateTimeAndConvertToLocale(item?.createdAt)?.localDate}</span></div>
              <div className="h-px bg-[#F1F1F1] my-2" />
              <div className="flex justify-between text-[11px] text-[#8C8C8C]"><span>Withdrawal Amount</span><span className="text-[#303030] font-semibold">${item?.amount}</span></div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 px-4">
            <p className="text-[14px] font-poppins font-[500] text-[#303030] mb-2">
              No Withdrawals Yet
            </p>
            <p className="text-[12px] font-poppins font-[400] text-[#767676]">
              Your withdrawal history will appear here once you request funds.
            </p>
          </div>
        )}
      </div>

      {/* Table on tablet/desktop */}
      <TableContainer className="hidden sm:block w-[100%] overflow-x-auto mt-2">
        <Table
          sx={{ borderCollapse: "separate", borderSpacing: "0px 8px", minWidth: 640 }}
          aria-label="simple table"
        >
          <TableHead className="bg-[#00ADEF] w-full">
            <TableRow
              className="h-[50px]"
              sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
            >
              {columns.map((column) => (
                <TableCell key={column.name} className="!text-[#FFFFFF]">{column.name}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {debit && debit?.length > 0 ? (
              debit?.map((funds, index) => (
                <TableRow
                  key={index}
                  className="h-[50px]"
                  sx={{
                    boxShadow: "0px 4px 4px 0px #00000040",
                    backgroundColor: (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                  }}
                >
                  <TableCell>{funds?._id}</TableCell>
                  <TableCell>{separateDateTimeAndConvertToLocale(funds?.createdAt)?.localDate}</TableCell>
                  <TableCell>${funds?.amount}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" className="!py-8">
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-[16px] font-poppins font-[500] text-[#303030] mb-2">
                      No Withdrawals Yet
                    </p>
                    <p className="text-[14px] font-poppins font-[400] text-[#767676] max-w-md">
                      Your withdrawal history will appear here once you request funds.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination totalPages={totalPages} handlePageChange={handlePageChange} />
    </div>
  );
};

export default FundsDepositedTable;
